import React from 'react';

/**
 * مكون لعرض رقم على السوروبان المتقدم
 * يستخدم نفس تصميم السوروبان التفاعلي مع الحلقات السداسية والألوان الواقعية
 */
const SorobanDisplay = ({ number, digitsPerNumber = 1, size = 'large' }) => {
  // تحويل الرقم إلى مصفوفة من الأرقام
  const digits = String(number).padStart(digitsPerNumber, '0').split('').map(Number);
  
  // إعدادات الحجم المتقدمة (نفس السوروبان التفاعلي)
  const sizes = {
    small: { 
      DISTANCE_RODS: 40, TOP_MARGIN: 10, NUMBER_HEIGHT: 0, LEFT_MARGIN: 8,
      FRAME_LINE_WIDTH: 8, ROD_LINE_WIDTH: 4, DOT_SIZE: 2, BEAD_WIDTH: 38, BEAD_HEIGHT: 20
    },
    medium: { 
      DISTANCE_RODS: 50, TOP_MARGIN: 10, NUMBER_HEIGHT: 0, LEFT_MARGIN: 10,
      FRAME_LINE_WIDTH: 8, ROD_LINE_WIDTH: 5, DOT_SIZE: 2.5, BEAD_WIDTH: 47, BEAD_HEIGHT: 25
    },
    large: { 
      DISTANCE_RODS: 60, TOP_MARGIN: 10, NUMBER_HEIGHT: 0, LEFT_MARGIN: 10,
      FRAME_LINE_WIDTH: 10, ROD_LINE_WIDTH: 6, DOT_SIZE: 3, BEAD_WIDTH: 56, BEAD_HEIGHT: 30
    },
    xlarge: { 
      DISTANCE_RODS: 70, TOP_MARGIN: 10, NUMBER_HEIGHT: 0, LEFT_MARGIN: 12,
      FRAME_LINE_WIDTH: 12, ROD_LINE_WIDTH: 7, DOT_SIZE: 3.5, BEAD_WIDTH: 65, BEAD_HEIGHT: 35
    }
  };

  const config = sizes[size] || sizes.large;
  const HEAVEN = config.BEAD_HEIGHT * 2 + config.FRAME_LINE_WIDTH;
  const EARTH = config.BEAD_HEIGHT * 5;
  const HEIGHT = HEAVEN + EARTH + config.FRAME_LINE_WIDTH;
  const totalWidth = config.DISTANCE_RODS * (digits.length + 1);

  // حساب موضع الحلقات بناءً على الرقم
  const getBeadPositions = (digit) => {
    const positions = {
      upperBead: false,
      lowerBeads: [false, false, false, false]
    };

    if (digit >= 5) {
      positions.upperBead = true;
      const remainder = digit - 5;
      for (let i = 0; i < remainder; i++) {
        positions.lowerBeads[i] = true;
      }
    } else {
      positions.upperBead = false;
      for (let i = 0; i < digit; i++) {
        positions.lowerBeads[i] = true;
      }
    }

    return positions;
  };

  // رسم الحلقة السداسية (نفس تصميم السوروبان التفاعلي)
  const renderHexagonBead = (centerX, centerY, isActive, isUpper = false) => {
    const points = [];
    const halfWidth = config.BEAD_WIDTH / 2;
    const halfHeight = config.BEAD_HEIGHT / 2;
    
    // إنشاء النقاط السداسية
    points.push({ x: centerX - halfWidth, y: centerY });
    points.push({ x: centerX + halfWidth, y: centerY });
    points.push({ x: centerX + halfWidth / 3, y: centerY - halfHeight });
    points.push({ x: centerX - halfWidth / 3, y: centerY - halfHeight });
    points.push({ x: centerX - halfWidth, y: centerY });
    points.push({ x: centerX - halfWidth / 3, y: centerY + halfHeight });
    points.push({ x: centerX + halfWidth / 3, y: centerY + halfHeight });
    points.push({ x: centerX + halfWidth, y: centerY });

    const color = isActive ? '#228B22' : '#90EE90'; // Forest green for active, light green for inactive
    const strokeColor = '#000000';

    return (
      <g>
        {/* ظل الحلقة */}
        <polygon
          points={points.map(p => `${p.x + 3},${p.y + 3}`).join(' ')}
          fill="rgba(0,0,0,0.5)"
          opacity="0.3"
        />
        {/* الحلقة الرئيسية */}
        <polygon
          points={points.map(p => `${p.x},${p.y}`).join(' ')}
          fill={color}
          stroke={strokeColor}
          strokeWidth="1"
        />
      </g>
    );
  };

  // رسم عمود واحد من السوروبان المتقدم
  const renderColumn = (digit, columnIndex) => {
    const positions = getBeadPositions(digit);
    const xPos = config.LEFT_MARGIN + (columnIndex + 1) * config.DISTANCE_RODS;
    const topFrame = config.TOP_MARGIN + config.NUMBER_HEIGHT;

    return (
      <g key={columnIndex}>
        {/* العمود الرأسي */}
        <line
          x1={xPos}
          y1={topFrame}
          x2={xPos}
          y2={topFrame + HEIGHT}
          stroke="rgba(212,85,0,0.5)"
          strokeWidth={config.ROD_LINE_WIDTH}
        />

        {/* الحلقة العلوية */}
        {renderHexagonBead(
          xPos,
          positions.upperBead 
            ? topFrame + HEAVEN - config.BEAD_HEIGHT / 2 - config.FRAME_LINE_WIDTH / 2
            : topFrame + config.BEAD_HEIGHT / 2 + config.FRAME_LINE_WIDTH / 2,
          positions.upperBead,
          true
        )}

        {/* الحلقات السفلية */}
        {(() => {
          const activeBeads = [];
          const inactiveBeads = [];
          
          // فصل الخرزات المفعلة وغير المفعلة
          positions.lowerBeads.forEach((isActive, beadIndex) => {
            if (isActive) {
              activeBeads.push(beadIndex);
            } else {
              inactiveBeads.push(beadIndex);
            }
          });
          
          const beads = [];
          
          // إضافة الخرزات المفعلة أولاً (ملتصقة بالخط الفاصل)
          activeBeads.forEach((beadIndex, activeIndex) => {
            const beadY = topFrame + HEAVEN + config.FRAME_LINE_WIDTH / 2 + config.BEAD_HEIGHT / 2 + activeIndex * config.BEAD_HEIGHT;
            beads.push(
              <g key={`active-${beadIndex}`}>
                {renderHexagonBead(xPos, beadY, true, false)}
              </g>
            );
          });
          
          // إضافة الخرزات غير المفعلة (ملتصقة بالأسفل)
          inactiveBeads.forEach((beadIndex, inactiveIndex) => {
            const beadY = topFrame + HEAVEN + EARTH - config.FRAME_LINE_WIDTH / 2 - config.BEAD_HEIGHT / 2 - inactiveIndex * config.BEAD_HEIGHT;
            beads.push(
              <g key={`inactive-${beadIndex}`}>
                {renderHexagonBead(xPos, beadY, false, false)}
              </g>
            );
          });
          
          return beads;
        })()}
      </g>
    );
  };

  return (
    <div className="flex flex-col items-center">
      <svg
        width={totalWidth + 2 * config.LEFT_MARGIN}
        height={config.TOP_MARGIN + config.NUMBER_HEIGHT + HEIGHT + 10}
        viewBox={`0 0 ${totalWidth + 2 * config.LEFT_MARGIN} ${config.TOP_MARGIN + config.NUMBER_HEIGHT + HEIGHT + 10}`}
        className="soroban-display"
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '2px solid #333'
        }}
      >
        {/* الإطار الخارجي */}
        <rect
          x={config.LEFT_MARGIN}
          y={config.TOP_MARGIN + config.NUMBER_HEIGHT}
          width={totalWidth}
          height={HEIGHT}
          fill="none"
          stroke="#1E40AF"
          strokeWidth={config.FRAME_LINE_WIDTH}
        />

        {/* الخط الفاصل الأفقي */}
        <line
          x1={config.LEFT_MARGIN + config.FRAME_LINE_WIDTH / 2}
          y1={config.TOP_MARGIN + config.NUMBER_HEIGHT + HEAVEN}
          x2={config.LEFT_MARGIN + totalWidth - config.FRAME_LINE_WIDTH / 2}
          y2={config.TOP_MARGIN + config.NUMBER_HEIGHT + HEAVEN}
          stroke="#1E40AF"
          strokeWidth={config.FRAME_LINE_WIDTH}
        />

        {/* النقاط الفاصلة للآلاف */}
        {digits.map((_, index) => {
          const xPos = config.LEFT_MARGIN + (index + 1) * config.DISTANCE_RODS;
          const middle = Math.floor(digits.length / 2);
          if ((index - middle) % 3 === 0) {
            return (
              <circle
                key={`dot-${index}`}
                cx={xPos}
                cy={config.TOP_MARGIN + config.NUMBER_HEIGHT + HEAVEN}
                r={config.DOT_SIZE}
                fill="#FFFFFF"
                stroke="#000000"
                strokeWidth="1"
              />
            );
          }
          return null;
        })}

        {/* رسم الأعمدة */}
        {digits.map((digit, index) => renderColumn(digit, index))}
      </svg>
    </div>
  );
};

export default SorobanDisplay;